// AlarmHubPage.js
import React, { Suspense, useState, useMemo, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  CircularProgress,
  Card,
  Typography,
  Collapse,
  Button
} from '@mui/material';

import { 
  useGetAlarmsByAccountQuery, 
  useUpdateAlarmMutation, 
  useGetAlarmsFeedByAccountQuery,
  useGetAlarmTypesQuery 
} from '../../../api/recalc/endpoints/alarmsApiSlice';

import { 
  useGetAlarmGroupsByAccountQuery,
  usePostAlarmGroupMutation,
  usePatchAlarmGroupMutation,
  useDeleteAlarmGroupMutation
} from '../../../api/recalc/endpoints/alarmGroupsApiSlice';

import { getSelectedAccount } from '../../../redux/selectedAccountSlice';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';


import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/snackbarSlice';

import { useLazyGetUnitByIdQuery } from '../../../api/recalc/endpoints/unitsApiSlice';
import { useLazyGetSensorsByIdQuery } from '../../../api/recalc/endpoints/sensorsApiSlice';
import { useLazyGetSubscriptionByIdQuery } from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';
import { useLazyGetBuildingByIdQuery } from '../../../api/recalc/endpoints/buildingApiSlice';
import { useLazyGetGatewayByIdQuery } from '../../../api/recalc/endpoints/gatewayApiSlice';
import { useLazyGetDataQuery } from '../../../api/recalc/endpoints/dataApiSlice';

const AlarmSeverityAreaChart = React.lazy(() => import('remote/AlarmSeverityAreaChart'));
const AlarmCalendarHeatmap = React.lazy(() => import('remote/AlarmCalendarHeatmap'));
const AlarmDataGrid = React.lazy(() => import('remote/AlarmDataGrid'));
const AlarmDetailDrawer = React.lazy(() => import('remote/AlarmDetailDrawer'));
const CreateAlarmGroupDialog = React.lazy(() => import('remote/CreateAlarmGroupDialog'));
const EditAlarmGroupDialog = React.lazy(() => import('remote/EditAlarmGroupDialog'));

const formatAndEncodeTimestampForAPI = (date) => {
  const isoString = DateTime.fromJSDate(date, { zone: 'utc' }).toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'");
  return encodeURIComponent(isoString);
};

const toUTC = (timestamp) => DateTime.fromISO(timestamp, { zone: 'utc' }).toMillis();



const AlarmHubPage = () => {
  const selectedAccount = useSelector(getSelectedAccount);
  const dispatch = useDispatch();

  const { data: alarmGroups, isLoading: isLoadingAlarmGroups, isFetching: isFetchingAlarmGroups } = useGetAlarmGroupsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId });
  const [postAlarmGroup] = usePostAlarmGroupMutation();
  const [patchAlarmGroup] = usePatchAlarmGroupMutation();
  const [deleteAlarmGroup] = useDeleteAlarmGroupMutation();
  const { data: alarmFeed, isLoading: isLoadingAlarmFeed, isFetching: isFetchingAlarmFeed } = useGetAlarmsFeedByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId, pollingInterval: 60000, skipPollingIfUnfocused: true });
  const { data: alarms, isLoading: isLoadingAlarms, isFetching: isFetchingAlarms } = useGetAlarmsByAccountQuery(selectedAccount?.AccountId, { skip: !selectedAccount?.AccountId, pollingInterval: 60000, skipPollingIfUnfocused: true });
  const { data: alarmTypes, isLoading: isLoadingAlarmTypes, isFetching: isFetchingAlarmTypes } = useGetAlarmTypesQuery();
  
  const [updateAlarm] = useUpdateAlarmMutation();
  const [getUnitById, { isLoading: isLoadingUnits, isFetching: isFetchingUnits }] = useLazyGetUnitByIdQuery();
  const [getSensorsById, { isLoading: isLoadingSensors, isFetching: isFetchingSensors }] = useLazyGetSensorsByIdQuery();
  const [getSubscriptionById, { isLoading: isLoadingSubscriptions, isFetching: isFetchingSubscriptions }] = useLazyGetSubscriptionByIdQuery();
  const [getBuildingById, { isLoading: isLoadingBuildings, isFetching: isFetchingBuildings }] = useLazyGetBuildingByIdQuery();
  const [getGatewayById, { isLoading: isLoadingGateways, isFetching: isFetchingGateways }] = useLazyGetGatewayByIdQuery();
  const [getData, { isLoading: isLoadingData, isFetching: isFetchingData }] = useLazyGetDataQuery();


  const [visibleSeverities, setVisibleSeverities] = useState({});
  const [selectedSeverity, setSelectedSeverity] = useState([]);
  const [selectedState, setSelectedState] = useState('all');
  const [selectedAlarmType, setSelectedAlarmType] = useState('all');

  const [openAlarmDetailDrawer, setOpenAlarmDetailDrawer] = useState(false);
  const [openCreateAlarmGroupDialog, setOpenCreateAlarmGroupDialog] = useState(false);
  const [openEditAlarmGroupDialog, setOpenEditAlarmGroupDialog] = useState(false);

  const [selectedAlarmSelection, setSelectedAlarmSelection] = useState(null);

  const [selectedAlarmAssociationContent, setSelectedAlarmAssociationContent] = useState(null);

  const [dateRange, setDateRange] = useState([
    DateTime.utc().startOf('year'), // January 1st, 00:00 UTC
    DateTime.utc()                  // current date and time UTC
  ]);



  useEffect(() => {
    if (alarmFeed) {
      const initial = {};
      alarmFeed.forEach(a => {
        const s = a.Severity || 'Unresolved';
        initial[s] = true;
      });
      setVisibleSeverities(initial);
    }
  }, [alarmFeed]);

  const toggleSeverity = (severity) => {
    setVisibleSeverities(prev => ({
      ...prev,
      [severity]: !prev[severity]
    }));
  };

  const availableAlarmTypes = useMemo(() => {
    if (!alarmFeed) return [];
    return Array.from(new Set(alarmFeed.map(a => a.AlarmType))).sort();
  }, [alarmFeed]);

  const { groupedCounts, filteredAlarms } = useMemo(() => {
    const groupedCounts = {
      Information: 0,
      Trivial: 0,
      Minor: 0,
      Medium: 0,
      Major: 0,
      Critical: 0,
    };

    const filteredAlarms = [];

    if (alarmFeed?.length > 0) {
      for (const alarm of alarmFeed) {
        const created = DateTime.fromISO(alarm.Created, { zone: 'utc' });
        if (created < dateRange[0] || created > dateRange[1]) continue;
        if (selectedAlarmType !== 'all' && alarm.AlarmType !== selectedAlarmType) continue;

        const severity = alarm.Severity;
        if (!visibleSeverities[alarm.Severity]) continue;
        if (selectedState !== 'all' && alarm.State !== selectedState) continue;

        if (groupedCounts[severity] !== undefined) {
          groupedCounts[severity]++;
        }
        filteredAlarms.push(alarm);
      }
    }

    return { groupedCounts, filteredAlarms };
  }, [alarmFeed, selectedSeverity, selectedState, selectedAlarmType, dateRange, visibleSeverities]);

  const handleAlarmUpdate = async (alarmId, updatedAlarm) => {
    try {
      await updateAlarm({ alarmId, alarm: updatedAlarm });
      dispatch(showSnackbar({
        message: 'Alarm updated successfully',
        severity: 'success',
      }));
    } catch (error) {
      console.error('Error updating alarm:', error);
      dispatch(showSnackbar({
        message: 'Failed to update alarm',
        severity: 'error',
      }));
    }
  };

  const handleAlarmDelete = async (alarmId) => {
    try {
      await deleteAlarmGroup(alarmId).unwrap();
      dispatch(showSnackbar({
        message: 'Alarm group deleted successfully',
        severity: 'success',
      }));
    } catch (error) {
      console.error('Error deleting alarm group:', error);
      dispatch(showSnackbar({
        message: 'Failed to delete alarm group',
        severity: 'error',
      }));
    }
  };


  const [showGraphs, setShowGraphs] = useState(true);

  const handleToggleGraphs = () => {
    setShowGraphs(prev => !prev);
  }

  useEffect(() => {
    if (!selectedAlarmSelection?.associations || !selectedAlarmSelection.alarm) return;

    const { Units, Sensors, Buildings, JobSubscriptions, Gateways } = selectedAlarmSelection.associations;
    const selectedAlarm = selectedAlarmSelection.alarm;

  
    const fetchAssociations = async () => {
      try {
        if (Units.length > 0) {
          const units = await Promise.all(Units.map(id => getUnitById(id).unwrap()));
        
          if (selectedAlarm?.DataFrom && selectedAlarm?.DataTo) {
            const from = DateTime.fromISO(selectedAlarm.DataFrom).minus({ hours: 12 }).toMillis();
            const to = DateTime.fromISO(selectedAlarm.DataTo).plus({ hours: 12 }).toMillis();
        
            const seriesPerUnit = {};
        
            await Promise.all(units.map(async (unit) => {
        
              const sensorSeries = await Promise.all(
                (unit.UnitSensors ?? []).map(async (unitSensor) => {
                  if (!unitSensor.Sensor || !unitSensor.UnitTypeInput) {
                    console.warn("❌ Skipping sensor due to missing fields:", {
                      unitSensorId: unitSensor.UnitSensorId,
                      hasSensor: !!unitSensor.Sensor,
                      hasUnitTypeInput: !!unitSensor.UnitTypeInput
                    });
                    return null;
                  }
        
                  try {
                    const fetchedData = await getData({
                      from: formatAndEncodeTimestampForAPI(new Date(from)),
                      to: formatAndEncodeTimestampForAPI(new Date(to)),
                      unitId: unit.UnitId,
                      datatype: 'temperature',
                      unitTypeInputId: unitSensor.UnitTypeInput.UnitTypeInputId,
                      aggregateType: 'hour',
                    }).unwrap(); // 👈 unwrap the promise returned by the trigger
        
                    if (!fetchedData || fetchedData.length === 0) {
                      console.warn("⚠️ No data returned for", unit.UnitId, unitSensor.SensorId);
                      return null;
                    }
        
                    return {
                      name: `${unitSensor?.CustomName || 'Sensor'} (ID: ${unit.UnitId})`,
                      base: fetchedData[0]?.Base,
                      data: fetchedData.map(item => [
                        toUTC(item.Timestamp),
                        parseFloat(item.Value.toFixed(2)),
                        item.Base,
                      ]),
                      type: 'spline',
                      showInNavigator: true,
                    };
                  } catch (e) {
                    console.error(`❌ Failed fetching data for Unit ${unit.UnitId}, Sensor ${unitSensor.SensorId}`, e);
                    return null;
                  }
                })
              );
        
              const filteredSeries = sensorSeries.filter(Boolean);
              seriesPerUnit[unit.UnitId] = filteredSeries;
        
            }));
        
            const unitsWithData = units.map(unit => ({
              ...unit,
              Data: seriesPerUnit[unit.UnitId] || []
            }));
        
        
            setSelectedAlarmAssociationContent(prev => ({
              ...prev,
              Units: unitsWithData,
            }));
          } else {
            // No DataFrom/DataTo — fallback to setting units only
            setSelectedAlarmAssociationContent(prev => ({
              ...prev,
              Units: units,
            }));
          }
        } else {
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            Units: [],
          }));
        }
  
        if (Sensors.length > 0) {
          const sensors = await Promise.all(Sensors.map(id => getSensorsById(id).unwrap()));
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            Sensors: sensors,
          }));
        } else {
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            Sensors: [],
          }));
        }
  
        if (Buildings.length > 0) {
          const buildings = await Promise.all(Buildings.map(id => getBuildingById(id).unwrap()));
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            Buildings: buildings,
          }));
        } else {
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            Buildings: [],
          }));
        }
  
        if (JobSubscriptions.length > 0) {
          const subscriptions = await Promise.all(JobSubscriptions.map(id => getSubscriptionById({ id }).unwrap()));
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            JobSubscriptions: subscriptions,
          }));
        } else {
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            JobSubscriptions: [],
          }));
        }
  
        if (Gateways.length > 0) {
          const gateways = await Promise.all(Gateways.map(id => getGatewayById(id).unwrap()));
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            Gateways: gateways,
          }));
        } else {
          setSelectedAlarmAssociationContent(prev => ({
            ...prev,
            Gateways: [],
          }));
        }
      } catch (error) {
        console.error("Error fetching associations:", error);
      }
    };
  
    const hasAnyAssociations =
      Units?.length > 0 ||
      Sensors?.length > 0 ||
      Buildings?.length > 0 ||
      JobSubscriptions?.length > 0 ||
      Gateways?.length > 0;
  
    if (hasAnyAssociations) {
      fetchAssociations();
    }
  
  }, [selectedAlarmSelection]);


  const handleAlarmGroupCreate = async (alarmGroup) => {
    try {
      const newGroup = await postAlarmGroup(alarmGroup).unwrap();
      dispatch(showSnackbar({
        message: 'Alarm group created successfully',
        severity: 'success',
      }));
      setOpenCreateAlarmGroupDialog(false);
    } catch (error) {
      console.error('Error creating alarm group:', error);
      dispatch(showSnackbar({
        message: 'Failed to create alarm group',
        severity: 'error',
      }));
      setOpenCreateAlarmGroupDialog(false);
    }
  };

  const handleAlarmGroupUpdate = async (alarmGroup) => {
    try {
      const updatedGroup = await patchAlarmGroup(alarmGroup).unwrap();
      dispatch(showSnackbar({
        message: 'Alarm group updated successfully',
        severity: 'success',
      }));
      setOpenEditAlarmGroupDialog(false);
    } catch (error) {
      console.error('Error updating alarm group:', error);
      dispatch(showSnackbar({
        message: 'Failed to update alarm group',
        severity: 'error',
      }));
      setOpenEditAlarmGroupDialog(false);
    }
  }

  return (
    <Container maxWidth='xxl'>

      <Button onClick={handleToggleGraphs} sx={{ textTransform: 'none', mb: 1, color: 'text.secondary' }} variant="text">
        {showGraphs ? 'Skjul grafer' : 'Vis grafer'}
      </Button>

      <Collapse in={showGraphs} timeout={500}>
        <Grid container spacing={2}>
          <Grid xs={12} md={6} item>
            <Card variant="outlined" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, maxHeight: 340 }}>
              {filteredAlarms && filteredAlarms.length > 0 ? (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}><CircularProgress /></Box>}>
                  <AlarmSeverityAreaChart
                    alarms={filteredAlarms}
                    visibleSeverities={visibleSeverities}
                    onToggleSeverity={toggleSeverity}
                  />
                </Suspense>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                  <Typography>No alarms found for the selected filters.</Typography>
                </Box>
              )}
            </Card>
          </Grid>
          <Grid xs={12} md={6} item>
            <Card variant="outlined" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, maxHeight: 340 }}>
              {filteredAlarms && filteredAlarms.length > 0 ? (
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}><CircularProgress /></Box>}>
                  <AlarmCalendarHeatmap alarms={filteredAlarms} />
                </Suspense>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300 }}>
                  <Typography>No alarms found for the selected filters.</Typography>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Collapse>

      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}><CircularProgress /></Box>}>
        <AlarmDataGrid
          alarms={filteredAlarms}
          alarmGroups={alarmGroups}
          severityFilter={selectedSeverity}
          onSeverityFilterChange={setSelectedSeverity}
          setSelectedAlarmSelection={setSelectedAlarmSelection}
          selectedAlarmSelection={selectedAlarmSelection}
          stateFilter={selectedState}
          onStateFilterChange={setSelectedState}
          handleAlarmUpdate={handleAlarmUpdate}
          alarmTypeFilter={selectedAlarmType}
          onAlarmTypeFilterChange={setSelectedAlarmType}
          availableAlarmTypes={availableAlarmTypes}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          visibleSeverities={visibleSeverities}
          onToggleSeverity={toggleSeverity}
          setOpenAlarmDetailDrawer={setOpenAlarmDetailDrawer}
          setOpenCreateAlarmGroupDialog={setOpenCreateAlarmGroupDialog}
          setOpenEditAlarmGroupDialog={setOpenEditAlarmGroupDialog}
          groupedCounts={groupedCounts}
          showGraphs={showGraphs}
          selectedAlarmAssociationContent={selectedAlarmAssociationContent}
          isLoading={
            isLoadingAlarms ||
            isFetchingAlarms ||
            isLoadingSensors ||
            isFetchingSensors ||
            isLoadingUnits ||
            isFetchingUnits ||
            isLoadingSubscriptions ||
            isFetchingSubscriptions ||
            isLoadingBuildings ||
            isFetchingBuildings ||
            isLoadingGateways ||
            isFetchingGateways ||
            isLoadingData ||
            isFetchingData
          }
        />
      </Suspense>

      {selectedAccount && (
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}><CircularProgress /></Box>}>
        <AlarmDetailDrawer
          open={openAlarmDetailDrawer}
          userOptions={selectedAccount?.UserRoles}
          alarmGroups={alarmGroups}
          alarmTypes={alarmTypes}
          onClose={() => setOpenAlarmDetailDrawer(false)}
        />
      </Suspense>
      )}

    {selectedAccount && (
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}><CircularProgress /></Box>}>
        <CreateAlarmGroupDialog
          selectedAccount={selectedAccount}
          open={openCreateAlarmGroupDialog}
          onClose={() => setOpenCreateAlarmGroupDialog(false)}
          onCreate={handleAlarmGroupCreate}
          userOptions={selectedAccount?.UserRoles}
          alarmTypeOptions={alarmTypes}
        />
      </Suspense>
    )}

    {selectedAccount && (
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}><CircularProgress /></Box>}>
        <EditAlarmGroupDialog
          selectedAccount={selectedAccount}
          open={openEditAlarmGroupDialog}
          onClose={() => setOpenEditAlarmGroupDialog(false)}
          openCreate={setOpenCreateAlarmGroupDialog}
          onUpdate={handleAlarmGroupUpdate}
          onDelete={handleAlarmDelete}
          userOptions={selectedAccount?.UserRoles}
          alarmTypeOptions={alarmTypes}
          alarmGroups={alarmGroups}
        />
      </Suspense>
    )}

    </Container>
  );
};

export default AlarmHubPage;